import { ResolvedTheme } from 'helpers/theme'
import { DEFAULT_HEADER_BACKGROUND_COLOUR, DEFAULT_HEADER_TEXT_COLOUR } from 'helpers/headerColours'

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

/**
 * Channels as 0-255, or null for anything that isn't a plain hex colour.
 *
 * Returning null rather than throwing matters: the colour that reaches these
 * helpers is often not a colour at all but `inherit`, `none`, or a
 * `var(--color-…)` fallback from the cell colouring helpers. Those must pass
 * straight through untouched.
 */
export const parseHex = (value: string): [number, number, number] | null => {
  const match = HEX_PATTERN.exec((value || '').trim())
  if (!match) return null

  const digits = match[1]
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map((d) => d + d)
          .join('')
      : digits

  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)]
}

export const toHex = (rgb: [number, number, number]): string =>
  '#' +
  rgb
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')

export const rgbToHsl = ([r, g, b]: [number, number, number]): [number, number, number] => {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255]
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const lightness = (max + min) / 2
  const delta = max - min

  if (delta === 0) return [0, 0, lightness]

  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)
  const hue =
    max === rn ? (gn - bn) / delta + (gn < bn ? 6 : 0) : max === gn ? (bn - rn) / delta + 2 : (rn - gn) / delta + 4

  return [hue * 60, saturation, lightness]
}

const hslToRgb = ([h, s, l]: [number, number, number]): [number, number, number] => {
  if (s === 0) return [l * 255, l * 255, l * 255]

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue = (((h % 360) + 360) % 360) / 360

  const channel = (t: number): number => {
    const shifted = t < 0 ? t + 1 : t > 1 ? t - 1 : t
    if (shifted < 1 / 6) return p + (q - p) * 6 * shifted
    if (shifted < 1 / 2) return q
    if (shifted < 2 / 3) return p + (q - p) * (2 / 3 - shifted) * 6
    return p
  }

  return [channel(hue + 1 / 3) * 255, channel(hue) * 255, channel(hue - 1 / 3) * 255]
}

const relativeLuminance = ([r, g, b]: [number, number, number]): number => {
  const [rl, gl, bl] = [r / 255, g / 255, b / 255].map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4))
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export const contrastRatio = (a: string, b: string): number => {
  const [rgbA, rgbB] = [parseHex(a), parseHex(b)]
  if (!rgbA || !rgbB) return 1

  const [lumA, lumB] = [relativeLuminance(rgbA), relativeLuminance(rgbB)]
  const [lighter, darker] = lumA > lumB ? [lumA, lumB] : [lumB, lumA]
  return (lighter + 0.05) / (darker + 0.05)
}

export const contrastText = (hex: string): '#000' | '#fff' => {
  const rgb = parseHex(hex)
  if (!rgb) return '#000'
  return relativeLuminance(rgb) > 0.179 ? '#000' : '#fff'
}

/**
 * The lightness band that customer-chosen fills are mapped into for the dark
 * theme. The floor sits just above the darkest surface so a fill never merges
 * into the canvas; the ceiling stays well below white so a row of coloured cells
 * doesn't glare.
 */
const DARK_LIGHTNESS_FLOOR = 0.13
const DARK_LIGHTNESS_CEILING = 0.58

/** Past this, a hue reads as neon against a dark surface rather than as a tint. */
const DARK_SATURATION_CAP = 0.55

/**
 * Greys get a few points of the surface's blue so they sit in the same family as
 * the rest of the dark palette instead of looking like a separate, flatter grey.
 */
const NEUTRAL_HUE = 220
const NEUTRAL_SATURATION = 0.07
const NEUTRAL_THRESHOLD = 0.06

/**
 * Re-render a colour the customer picked against a white canvas so it still
 * works against a dark one.
 *
 * The stored hex is never touched — this is purely a rendering decision, so
 * switching back to the light theme restores exactly what they chose.
 *
 * Lightness is *inverted* into the dark band rather than merely darkened. What
 * matters about a fill is how far it stands out from the canvas behind it: a
 * near-white fill is nearly invisible on white and should stay nearly invisible
 * on black, while the near-black fill someone picked to shout should still
 * shout. Simply darkening everything would make the first two indistinguishable
 * and the third disappear entirely.
 */
export const adaptColourToTheme = (colour: string, theme: ResolvedTheme): string => {
  if (theme === 'light') return colour

  const rgb = parseHex(colour)
  if (!rgb) return colour

  const [hue, saturation, lightness] = rgbToHsl(rgb)
  const adaptedLightness = DARK_LIGHTNESS_FLOOR + (1 - lightness) * (DARK_LIGHTNESS_CEILING - DARK_LIGHTNESS_FLOOR)

  const isNeutral = saturation < NEUTRAL_THRESHOLD
  return toHex(
    hslToRgb([
      isNeutral ? NEUTRAL_HUE : hue,
      isNeutral ? NEUTRAL_SATURATION : Math.min(saturation, DARK_SATURATION_CAP),
      adaptedLightness
    ])
  )
}

/** Below this, text is uncomfortable to read at the sizes used in a grid. */
const MINIMUM_TEXT_CONTRAST = 4.5

/**
 * Keep the customer's font colour when it still reads against the adapted fill,
 * and fall back to plain black or white when it doesn't.
 *
 * Someone who set dark grey text on a pale yellow fill chose the *yellow*; the
 * text colour was only ever there to stay legible on it. Once the fill has
 * become dark, honouring the original text colour would make the cell
 * unreadable, which is not what they asked for.
 */
export const adaptFontColourToTheme = (fontColour: string, background: string, theme: ResolvedTheme): string => {
  if (theme === 'light') return fontColour
  if (!parseHex(fontColour) || !parseHex(background)) return fontColour

  return contrastRatio(fontColour, background) >= MINIMUM_TEXT_CONTRAST ? fontColour : contrastText(background)
}

/**
 * Shifts a colour's brightness by a percentage. Negative darkens.
 *
 * Used for the hub's brand colour on primary buttons and chart series. It lives
 * here rather than in style/utils so there is one place that knows how to take a
 * hex apart and put it back together — the previous copy open-coded both halves,
 * returned "#NaNNaNNaN" for anything that wasn't a six-digit hex, and clamped
 * only the top of the range, so a large negative amount produced a negative
 * channel and a malformed colour.
 */
export const tint = (hex: string, amount: number): string => {
  const rgb = parseHex(hex)
  if (!rgb) return hex

  return toHex(rgb.map((channel) => (channel * (100 + amount)) / 100) as [number, number, number])
}

/**
 * The darkest app surface, from the `.dark` block in style/tailwind.css. Text
 * adaptation is judged against this rather than against whatever panel it
 * happens to sit on — the panels differ by only a few points of lightness, and
 * using the darkest one means a colour that passes here passes everywhere.
 */
const DARK_SURFACE = '#16181c'

/** The band a too-dark text colour is lifted into. Short of pure white, which
 *  halates against a near-black background on long reading. */
const DARK_TEXT_FLOOR = 0.62
const DARK_TEXT_CEILING = 0.92

/**
 * Adapt a customer-chosen *text* colour — one that sits directly on the app
 * surface rather than on a fill of its own.
 *
 * This is the opposite problem to `adaptColourToTheme`. A fill is judged by how
 * far it stands out from the canvas, so its lightness is inverted into a dark
 * band. Text only has to be readable, so the rule here is contrast-driven:
 * anything that already reads against the dark surface is left exactly as it
 * was, and only colours that would disappear are lifted.
 *
 * That distinction matters. A hub whose headings are brand blue keeps brand-blue
 * headings in the dark; a hub whose headings are near-black gets them lifted to
 * a light grey rather than recoloured. Inverting unconditionally, as the fill
 * path does, would dim a white heading to mid-grey for no reason.
 *
 * Used for document heading styles (`project.headingStyles`), which are stored
 * hex picked against a white page.
 */
export const adaptTextColourToSurface = (colour: string, theme: ResolvedTheme): string => {
  if (theme === 'light') return colour

  const rgb = parseHex(colour)
  if (!rgb) return colour
  if (contrastRatio(colour, DARK_SURFACE) >= MINIMUM_TEXT_CONTRAST) return colour

  const [hue, saturation, lightness] = rgbToHsl(rgb)
  return toHex(
    hslToRgb([
      hue,
      Math.min(saturation, DARK_SATURATION_CAP),
      DARK_TEXT_FLOOR + (1 - lightness) * (DARK_TEXT_CEILING - DARK_TEXT_FLOOR)
    ])
  )
}

/**
 * The header colours every column is created with. They are the hub default in
 * api/app/models/project.py, migration 175 backfilled every existing column
 * with them, and each new column copies the hub's value, so most stored headers
 * hold exactly these two hexes.
 *
 * They are also, exactly, the light theme's surface-sunken and body tokens. That
 * is what makes it safe to treat a header that still holds them as "never
 * customised" and paint it with the tokens instead: in the light theme nothing
 * changes by a pixel, and in the dark theme the header follows the palette
 * rather than being run through the fill adaptation like a colour someone chose.
 */
export { DEFAULT_HEADER_BACKGROUND_COLOUR, DEFAULT_HEADER_TEXT_COLOUR }

const isSameHex = (a: string, b: string): boolean => {
  const [rgbA, rgbB] = [parseHex(a), parseHex(b)]
  return !!rgbA && !!rgbB && rgbA.every((channel, index) => channel === rgbB[index])
}

/**
 * The painted colours for a column header, from what is stored on the column.
 *
 * One place for this so the grid header, the form view, the transposed field
 * cell and anything else that draws a header agree on what counts as default.
 *
 * - An empty or default background becomes the surface-sunken token. Text on it
 *   becomes the body token when it is also the default; a custom text colour is
 *   kept, lifted only if it would disappear against the dark surface.
 * - A custom background is adapted like any other fill, and its text — default
 *   or not — is judged against that adapted fill. #333 on a custom yellow was
 *   chosen to read on the yellow, so it follows the yellow, not the theme.
 *
 * Only the exact defaults count. White or black headers are deliberate choices
 * (no code path has ever stored them as a default) and stay adapted as such.
 */
export const adaptHeaderColoursToTheme = (
  background: string | null | undefined,
  text: string | null | undefined,
  theme: ResolvedTheme
): { background: string; text: string } => {
  if (!background || isSameHex(background, DEFAULT_HEADER_BACKGROUND_COLOUR)) {
    // The default grey, as the theme's token: the same grey in the light theme,
    // and the dark theme's own grey in the dark one.
    return {
      background: 'var(--color-surface-sunken)',
      text:
        !text || isSameHex(text, DEFAULT_HEADER_TEXT_COLOUR)
          ? 'var(--color-body)'
          : adaptTextColourToSurface(text, theme)
    }
  }

  const fill = adaptColourToTheme(background, theme)
  return { background: fill, text: text ? adaptFontColourToTheme(text, fill, theme) : 'var(--color-body)' }
}
